import React, { useState, useEffect, useCallback } from 'react';
import { getBaseDrivers, removeDriverFromBase } from 'service/DriverRegistrationService';
import Notification from 'utils/Notifications';

const STATUS_COLOR = {
  'В погрузке': { bg: '#fff7ed', color: '#c2410c' },
  'В пути': { bg: '#eff6ff', color: '#1d4ed8' },
  'Свободен': { bg: '#f0fdf4', color: '#15803d' },
  'Заблокирован': { bg: '#fef2f2', color: '#dc2626' },
};

const BaseDriversPanel = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDriver, setConfirmDriver] = useState(null); // { id, name, status }
  const [removing, setRemoving] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBaseDrivers();
      setDrivers(res.data || []);
    } catch (e) {
      console.error('Failed to load base drivers', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load immediately on mount
  useEffect(() => { load(); }, [load]);

  const handleRemoveConfirm = async () => {
    if (!confirmDriver) return;
    setRemoving(true);
    try {
      await removeDriverFromBase(confirmDriver.id);
      setNotify({ isOpen: true, message: `${confirmDriver.name} убран с базы`, type: 'success' });
      setConfirmDriver(null);
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Ошибка при удалении водителя';
      setNotify({ isOpen: true, message: msg, type: 'error' });
      setConfirmDriver(null);
    } finally {
      setRemoving(false);
    }
  };

  const isBusy = (status) => status === 'В пути' || status === 'В погрузке';

  return (
    <div style={{ margin: '16px 0', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>

      {/* Toolbar row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
          {loading ? 'Загрузка...' : `${drivers.length} водител${drivers.length === 1 ? 'ь' : drivers.length > 1 && drivers.length < 5 ? 'я' : 'ей'} на базе`}
        </span>
        <button
          onClick={load}
          disabled={loading}
          style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 500 }}
        >
          {loading ? '...' : '↻ Обновить'}
        </button>
      </div>

      {/* Content */}
      {loading && drivers.length === 0 ? (
        <div style={{ padding: '28px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Загрузка...</div>
      ) : drivers.length === 0 ? (
        <div style={{ padding: '28px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Водителей на вашей базе нет</div>
      ) : (
          <div>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 110px 80px', gap: '0 8px', padding: '8px 20px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
              {['Водитель', 'Гос. номер', 'Статус', ''].map(h => (
                <span key={h} style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</span>
              ))}
            </div>

            {drivers.map((d, idx) => {
              const sc = STATUS_COLOR[d.status] || { bg: '#f3f4f6', color: '#374151' };
              const busy = isBusy(d.status);
              return (
                <div
                  key={d.id}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr 120px 110px 80px',
                    gap: '0 8px', padding: '12px 20px', alignItems: 'center',
                    borderBottom: idx < drivers.length - 1 ? '1px solid #f9fafb' : 'none',
                    background: idx % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.employee_name || '—'}
                    </p>
                    {d.employee_phone && (
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>{d.employee_phone}</p>
                    )}
                  </div>
                  <span style={{ fontSize: 13, color: '#374151' }}>{d.vehicle_number || '—'}</span>
                  <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, whiteSpace: 'nowrap', textAlign: 'center' }}>
                    {d.status || 'Свободен'}
                  </span>
                  <button
                    disabled={busy}
                    onClick={() => setConfirmDriver({ id: d.id, name: d.employee_name, status: d.status })}
                    title={busy ? `Нельзя убрать: ${d.status}` : 'Убрать с базы'}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1.5px solid',
                      borderColor: busy ? '#e5e7eb' : '#dc2626',
                      background: '#fff', color: busy ? '#d1d5db' : '#dc2626',
                      cursor: busy ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                    }}
                  >
                    Убрать
                  </button>
                </div>
              );
            })}
          </div>
        )
      }

      {/* Confirmation bottom-sheet */}
      {confirmDriver && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 9999 }}
          onClick={e => e.target === e.currentTarget && setConfirmDriver(null)}
        >
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', padding: '28px 24px 40px', width: '100%', maxWidth: 480, boxShadow: '0 -4px 30px rgba(0,0,0,0.15)' }}>
            <div style={{ width: 48, height: 4, background: '#e5e7eb', borderRadius: 4, margin: '0 auto 20px' }} />
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#111827' }}>Убрать водителя?</h3>
            <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14, lineHeight: 1.5 }}>
              <strong>{confirmDriver.name}</strong> будет отвязан от вашей базы. После этого водитель сможет самостоятельно выбрать новую базу.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDriver(null)}
                disabled={removing}
                style={{ padding: '10px 22px', borderRadius: 9, border: '1.5px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}
              >
                Отмена
              </button>
              <button
                onClick={handleRemoveConfirm}
                disabled={removing}
                style={{ padding: '10px 22px', borderRadius: 9, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}
              >
                {removing ? 'Убираем...' : 'Да, убрать'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
};

export default BaseDriversPanel;
